import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Coins, Plus, Power, Save, Undo2, Zap } from 'lucide-react';
import { adminSymbols } from '@/api/admin';
import type { AdminSymbol } from '@/api/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pill } from '@/components/widgets/Pill';
import { fmtDateTime } from '@/lib/format';
import { ApiError } from '@/lib/http';
import { toast } from '@/components/ui/sonner';
import { PresetSelector } from '@/features/symbols/PresetSelector';
import { ParamSliders } from '@/features/symbols/ParamSliders';
import { ApplyPendingNowDialog } from '@/features/symbols/ApplyPendingNowDialog';
import { AddSymbolWizard } from '@/features/symbols/AddSymbolWizard';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type Draft = {
  presetCode: string;
  maxTotalPosition: number;
  cooldownSeconds: number;
  note: string;
};

export function SymbolsPage() {
  const qc = useQueryClient();
  const symQ = useQuery({ queryKey: ['admin', 'symbols'], queryFn: adminSymbols.list });
  const presetsQ = useQuery({ queryKey: ['admin', 'param-presets'], queryFn: adminSymbols.paramPresets });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  // 初次加载时默认选第一行
  useEffect(() => {
    if (!selectedId && symQ.data?.length) setSelectedId(symQ.data[0]!.id);
  }, [symQ.data, selectedId]);

  const symbol = useMemo(() => symQ.data?.find((s) => s.id === selectedId), [symQ.data, selectedId]);

  return (
    <div className="p-6 space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-ink-0 flex items-center gap-2">
            <Coins className="h-5 w-5 text-brand" />
            品种与参数
          </h1>
          <p className="text-sm text-ink-2 mt-1">
            5 个预设组合(强约束) · 自由参数滑块 · 修改默认次日 03:05 生效 · 紧急覆盖立即应用(§9.9)
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4" />
          新增品种
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>品种列表 · {symQ.data?.length ?? 0}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg-3 text-ink-2 text-xs">
                <tr>
                  <th className="px-3 py-2 text-left font-normal">品种</th>
                  <th className="px-3 py-2 text-left font-normal">启用</th>
                  <th className="px-3 py-2 text-left font-normal">市场</th>
                  <th className="px-3 py-2 text-left font-normal">当前预设</th>
                  <th className="px-3 py-2 text-right font-normal">比例</th>
                  <th className="px-3 py-2 text-right font-normal">毛持仓预警</th>
                  <th className="px-3 py-2 text-right font-normal">冷却</th>
                  <th className="px-3 py-2 text-left font-normal">待生效</th>
                </tr>
              </thead>
              <tbody>
                {(symQ.data ?? []).map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => setSelectedId(s.id)}
                    className={
                      'border-t border-line cursor-pointer ' +
                      (selectedId === s.id ? 'bg-brand/5' : 'row-hover')
                    }
                  >
                    <td className="px-3 py-2 font-medium">{s.symbolName}</td>
                    <td className="px-3 py-2">
                      <Pill tone={s.enabled ? 'up' : 'neutral'}>{s.enabled ? '启用' : '禁用'}</Pill>
                    </td>
                    <td className="px-3 py-2">
                      {s.marketOpen == null ? (
                        <span className="text-ink-3 text-xs">—</span>
                      ) : (
                        <Pill tone={s.marketOpen ? 'up' : 'down'}>
                          {s.marketStatus ?? (s.marketOpen ? '交易中' : '休市')}
                        </Pill>
                      )}
                    </td>
                    <td className="px-3 py-2 text-ink-1">{s.currentPresetCode ?? '—'}</td>
                    <td className="px-3 py-2 text-right num">{s.followRatio.toFixed(2)}x</td>
                    <td className="px-3 py-2 text-right num">{s.maxTotalPosition.toFixed(1)}</td>
                    <td className="px-3 py-2 text-right num">{s.cooldownSeconds}s</td>
                    <td className="px-3 py-2">
                      {s.pending ? <Pill tone="warn">有待生效</Pill> : <span className="text-ink-3 text-xs">—</span>}
                    </td>
                  </tr>
                ))}
                {!symQ.data?.length && (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-ink-3">
                      暂无品种
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {symbol && (
        <SymbolEditor
          symbol={symbol}
          presets={presetsQ.data ?? []}
          onApplyNow={() => setApplyOpen(true)}
          onChangeApplied={() => qc.invalidateQueries({ queryKey: ['admin', 'symbols'] })}
        />
      )}

      {symbol && <OfficialHoursCard symbol={symbol} />}

      <ApplyPendingNowDialog symbolId={selectedId} open={applyOpen} onOpenChange={setApplyOpen} />
      <AddSymbolWizard open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}

function SymbolEditor({
  symbol,
  presets,
  onApplyNow,
  onChangeApplied,
}: {
  symbol: AdminSymbol;
  presets: import('@/api/types').ParamPreset[];
  onApplyNow: () => void;
  onChangeApplied: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(initDraft(symbol));
  const [disableOpen, setDisableOpen] = useState(false);

  // 切换品种时重新初始化 draft
  useEffect(() => {
    setDraft(initDraft(symbol));
  }, [symbol.id, symbol.currentPresetCode, symbol.followRatio, symbol.maxTotalPosition, symbol.cooldownSeconds]);

  const dirty = isDirty(draft, symbol);

  const setPreset = useMutation({
    mutationFn: () => adminSymbols.setPreset(symbol.id, { presetCode: draft.presetCode, note: draft.note }),
  });
  const updateParams = useMutation({
    mutationFn: () =>
      adminSymbols.update(symbol.id, {
        maxTotalPosition: draft.maxTotalPosition,
        cooldownSeconds: draft.cooldownSeconds,
        note: draft.note,
      }),
  });
  const cancelPending = useMutation({
    mutationFn: () => adminSymbols.cancelPending(symbol.id),
    onSuccess: () => {
      toast.success('已撤销待生效变更');
      onChangeApplied();
    },
    onError: (err) => toast.error('撤销失败', { description: err instanceof ApiError ? err.message : '' }),
  });
  const disable = useMutation({
    mutationFn: () => adminSymbols.disable(symbol.id),
    onSuccess: () => {
      toast.success('已停用', { description: '已立即停止跟单,并从雇员 App 品种列表隐藏' });
      setDisableOpen(false);
      onChangeApplied();
    },
    onError: (err) => toast.error('停用失败', { description: err instanceof ApiError ? err.message : '' }),
  });
  const enable = useMutation({
    mutationFn: () => adminSymbols.enable(symbol.id),
    onSuccess: () => {
      toast.success('已记录启用', { description: '将于次日 03:05 生效;可在「待生效」处撤销或紧急立即应用' });
      onChangeApplied();
    },
    onError: (err) => toast.error('启用失败', { description: err instanceof ApiError ? err.message : '' }),
  });

  // A2 方向上限(即时生效,独立于 pending 跟单参数)。输入框文本:空 / 0 = 关闭。
  const [dirCapInput, setDirCapInput] = useState<string>(
    symbol.maxDirectionalPosition != null ? String(symbol.maxDirectionalPosition) : '',
  );
  useEffect(() => {
    setDirCapInput(symbol.maxDirectionalPosition != null ? String(symbol.maxDirectionalPosition) : '');
  }, [symbol.id, symbol.maxDirectionalPosition]);
  const setDirectionalCap = useMutation({
    mutationFn: (val: number | null) =>
      adminSymbols.setDirectionalCap(symbol.id, { maxDirectionalPosition: val, note: draft.note }),
    onSuccess: () => {
      toast.success('单边压力预警线已即时生效');
      onChangeApplied();
    },
    onError: (err) => toast.error('保存失败', { description: err instanceof ApiError ? err.message : '' }),
  });
  const applyDirCap = () => {
    const v = dirCapInput.trim() === '' ? 0 : Number(dirCapInput);
    if (!Number.isFinite(v) || v < 0 || v > 5) {
      toast.error('请输入 0~5 之间的单边压力预警线(手),0 或留空 = 关闭');
      return;
    }
    setDirectionalCap.mutate(v <= 0 ? null : v);
  };

  // §16 交易服务费费率(即时生效,独立于 pending)。type 空 = 不收费。
  const [commTypeInput, setCommTypeInput] = useState<string>(symbol.commissionType ?? '');
  const [commRateInput, setCommRateInput] = useState<string>(
    symbol.commissionRate != null && symbol.commissionRate > 0 ? String(symbol.commissionRate) : '',
  );
  useEffect(() => {
    setCommTypeInput(symbol.commissionType ?? '');
    setCommRateInput(
      symbol.commissionRate != null && symbol.commissionRate > 0 ? String(symbol.commissionRate) : '',
    );
  }, [symbol.id, symbol.commissionType, symbol.commissionRate]);
  const setCommission = useMutation({
    mutationFn: (body: { commissionType: string | null; commissionRate: number }) =>
      adminSymbols.setCommission(symbol.id, { ...body, note: draft.note }),
    onSuccess: () => {
      toast.success('交易服务费费率已即时生效');
      onChangeApplied();
    },
    onError: (err) => toast.error('保存失败', { description: err instanceof ApiError ? err.message : '' }),
  });
  const applyCommission = () => {
    const t = commTypeInput.trim();
    if (t === '') {
      setCommission.mutate({ commissionType: null, commissionRate: 0 }); // 清空 = 不收费
      return;
    }
    const r = Number(commRateInput);
    if (!Number.isFinite(r) || r < 0) {
      toast.error('请输入有效的费率(≥0)');
      return;
    }
    setCommission.mutate({ commissionType: t, commissionRate: r });
  };

  const submit = async () => {
    const tasks: Promise<unknown>[] = [];
    if (draft.presetCode !== symbol.currentPresetCode) tasks.push(setPreset.mutateAsync());
    const paramChanged =
      draft.maxTotalPosition !== symbol.maxTotalPosition ||
      draft.cooldownSeconds !== symbol.cooldownSeconds;
    if (paramChanged) tasks.push(updateParams.mutateAsync());
    if (tasks.length === 0) {
      toast.info('没有需要提交的变更');
      return;
    }
    try {
      await Promise.all(tasks);
      toast.success(`已保存 ${tasks.length} 项变更`, { description: '将于次日 03:05 生效' });
      onChangeApplied();
    } catch (err) {
      toast.error('保存失败', { description: err instanceof ApiError ? err.message : '' });
    }
  };

  const resetDraft = () => setDraft(initDraft(symbol));
  const submitting = setPreset.isPending || updateParams.isPending;

  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-3">
          <span>{symbol.symbolName} · 跟单参数</span>
          <div className="flex items-center gap-2 text-xs font-normal">
            <Pill tone={symbol.enabled ? 'up' : 'neutral'}>{symbol.enabled ? '已启用' : '已停用'}</Pill>
            {dirty && <Pill tone="warn">本地未保存</Pill>}
            {symbol.pending && (
              <Pill tone="warn">
                待生效:{symbol.pending.effectiveAt ? fmtDateTime(symbol.pending.effectiveAt) : '次日 03:05'}
              </Pill>
            )}
            {symbol.enabled ? (
              <Button variant="warn" size="sm" onClick={() => setDisableOpen(true)} disabled={disable.isPending}>
                <Power className="h-3.5 w-3.5" />
                停用
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => enable.mutate()} disabled={enable.isPending}>
                <Power className="h-3.5 w-3.5" />
                {enable.isPending ? '提交中...' : '启用'}
              </Button>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {symbol.pending && (
          <div className="rounded-md border border-warn/40 bg-warn/10 px-4 py-3 flex items-start gap-3">
            <div className="flex-1 text-sm text-warn-dim space-y-1">
              <div className="font-semibold">该品种有待生效变更</div>
              <PendingDiff symbol={symbol} />
              {symbol.pending.note && <div className="text-xs text-ink-2">备注:{symbol.pending.note}</div>}
            </div>
            <div className="flex flex-col gap-1.5 shrink-0">
              <Button variant="outline" size="sm" onClick={() => cancelPending.mutate()} disabled={cancelPending.isPending}>
                <Undo2 className="h-3.5 w-3.5" />
                撤销
              </Button>
              <Button variant="warn" size="sm" onClick={onApplyNow}>
                <Zap className="h-3.5 w-3.5" />
                立即应用
              </Button>
            </div>
          </div>
        )}

        <section className="space-y-2">
          <div className="text-sm font-semibold text-ink-1">预设组合(5 选 1 强约束)</div>
          <PresetSelector
            presets={presets}
            value={draft.presetCode}
            onChange={(presetCode) => setDraft({ ...draft, presetCode })}
            disabled={submitting}
          />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-2">
            <div className="text-sm font-semibold text-ink-1">自由参数</div>
            <ParamSliders
              value={{
                maxTotalPosition: draft.maxTotalPosition,
                cooldownSeconds: draft.cooldownSeconds,
              }}
              onChange={(p) => setDraft({ ...draft, ...p })}
              disabled={submitting}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="symbol-note">备注(写入 audit_logs)</Label>
            <Input
              id="symbol-note"
              placeholder="变更原因 / 关联工单"
              value={draft.note}
              onChange={(e) => setDraft({ ...draft, note: e.target.value })}
              disabled={submitting}
            />
            <div className="text-xs text-ink-3 mt-3 leading-relaxed">
              <div className="font-semibold text-ink-2 mb-1">规则提示</div>
              · 保存默认写入 pending,次日 03:05(交易窗口前)cron 自动应用
              <br />· 紧急情况点「立即应用」需 4 字以上原因
              <br />· "撤销待生效"按钮在保存前后均可使用,可随时回滚
            </div>
          </div>
        </section>

        {/* 单边压力预警线:只告警,即时生效(不走 pending) */}
        <section className="space-y-2 rounded-md border border-line p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-ink-1">单边压力敞口预警线</div>
              <div className="text-xs text-ink-3 mt-0.5 leading-relaxed">
                某方向<strong>同向毛量</strong>(non-net)+ 本单超过后只触发后台预警，<strong>不会限制用户下单</strong>。
                <strong className="text-warn"> 即时生效</strong>,不走次日 pending。留空或 0 = 关闭。
              </div>
            </div>
            <Pill tone={symbol.maxDirectionalPosition != null ? 'warn' : 'neutral'}>
              {symbol.maxDirectionalPosition != null ? `预警线 ${symbol.maxDirectionalPosition.toFixed(2)} 手` : '未启用'}
            </Pill>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              step="0.01"
              min="0"
              max="5"
              placeholder="预警手数(0 或留空=关闭)"
              value={dirCapInput}
              onChange={(e) => setDirCapInput(e.target.value)}
              disabled={setDirectionalCap.isPending}
              className="w-48"
            />
            <Button variant="outline" size="sm" onClick={applyDirCap} disabled={setDirectionalCap.isPending}>
              {setDirectionalCap.isPending ? '应用中...' : '应用(即时)'}
            </Button>
          </div>
        </section>

        {/* §16 交易服务费费率:按用户名义手数 round-turn 收,平仓收、历史展示。即时生效。 */}
        <section className="space-y-2 rounded-md border border-line p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-ink-1">交易服务费费率(§16)</div>
              <div className="text-xs text-ink-3 mt-0.5 leading-relaxed">
                按<strong>用户名义手数 × 费率 × round-turn(开+平)</strong>,平仓时一次收、历史展示。
                XAUUSD 实测约 <strong>USD_PER_LOT 13</strong>(单边)。
                <strong className="text-warn"> 即时生效</strong>,类型留空 = 不收费。
              </div>
            </div>
            <Pill tone={symbol.commissionType ? 'warn' : 'neutral'}>
              {symbol.commissionType
                ? `${symbol.commissionType} ${symbol.commissionRate ?? 0}`
                : '未配(不收费)'}
            </Pill>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={commTypeInput}
              onChange={(e) => setCommTypeInput(e.target.value)}
              disabled={setCommission.isPending}
              className="h-9 rounded-md border border-line bg-surface px-2 text-sm text-ink-1"
            >
              <option value="">不收费</option>
              <option value="USD_PER_LOT">USD_PER_LOT(每手单边美元)</option>
              <option value="QUOTE_CCY_PER_LOT">QUOTE_CCY_PER_LOT(每手单边报价币)</option>
              <option value="USD_PER_MILLION">USD_PER_MILLION(每百万名义)</option>
              <option value="PERCENTAGE_OF_VALUE">PERCENTAGE_OF_VALUE(名义百分比)</option>
            </select>
            <Input
              type="number"
              step="0.000001"
              min="0"
              placeholder="单边费率"
              value={commRateInput}
              onChange={(e) => setCommRateInput(e.target.value)}
              disabled={setCommission.isPending || commTypeInput.trim() === ''}
              className="w-40"
            />
            <Button variant="outline" size="sm" onClick={applyCommission} disabled={setCommission.isPending}>
              {setCommission.isPending ? '应用中...' : '应用(即时)'}
            </Button>
          </div>
        </section>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-line">
          <Button variant="outline" disabled={!dirty || submitting} onClick={resetDraft}>
            重置
          </Button>
          <Button onClick={submit} disabled={!dirty || submitting}>
            <Save className="h-4 w-4" />
            {submitting ? '保存中...' : '保存待生效'}
          </Button>
        </div>
      </CardContent>
    </Card>

    <Dialog open={disableOpen} onOpenChange={(v) => !v && setDisableOpen(false)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-warn">
            <Power className="h-5 w-5" />
            停用 {symbol.symbolName}?
          </DialogTitle>
          <DialogDescription>
            停用将<strong>立即生效</strong>:该品种停止跟单,并从雇员 App 的品种列表隐藏。
            已有持仓不受影响,重新启用需走次日 03:05 生效。
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDisableOpen(false)} disabled={disable.isPending}>
            取消
          </Button>
          <Button variant="warn" onClick={() => disable.mutate()} disabled={disable.isPending}>
            {disable.isPending ? '停用中...' : '确认停用'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

function PendingDiff({ symbol }: { symbol: AdminSymbol }) {
  const p = symbol.pending!;
  const rows: Array<[string, string, string]> = [];
  if (p.followRatio != null && p.followRatio !== symbol.followRatio)
    rows.push(['跟单比例', symbol.followRatio.toFixed(2), p.followRatio.toFixed(2)]);
  if (p.maxTotalPosition != null && p.maxTotalPosition !== symbol.maxTotalPosition)
    rows.push(['毛持仓预警', symbol.maxTotalPosition.toFixed(1), p.maxTotalPosition.toFixed(1)]);
  if (p.cooldownSeconds != null && p.cooldownSeconds !== symbol.cooldownSeconds)
    rows.push(['冷却', `${symbol.cooldownSeconds}s`, `${p.cooldownSeconds}s`]);
  if (p.enabled != null && p.enabled !== symbol.enabled)
    rows.push(['启用', symbol.enabled ? '是' : '否', p.enabled ? '是' : '否']);
  if (rows.length === 0) return <div className="text-xs">(暂无字段差异显示)</div>;
  return (
    <div className="space-y-0.5 text-xs num">
      {rows.map(([label, before, after]) => (
        <div key={label}>
          {label}:<span className="text-ink-2 line-through ml-1">{before}</span>
          <span className="mx-1.5 text-ink-3">→</span>
          <span className="text-warn font-semibold">{after}</span>
        </div>
      ))}
    </div>
  );
}

function initDraft(s: AdminSymbol): Draft {
  return {
    presetCode: s.currentPresetCode ?? '',
    maxTotalPosition: s.maxTotalPosition,
    cooldownSeconds: s.cooldownSeconds,
    note: '',
  };
}

function isDirty(d: Draft, s: AdminSymbol): boolean {
  return (
    d.presetCode !== (s.currentPresetCode ?? '') ||
    d.maxTotalPosition !== s.maxTotalPosition ||
    d.cooldownSeconds !== s.cooldownSeconds
  );
}

const WEEKDAYS = ['', '周一', '周二', '周三', '周四', '周五', '周六', '周日'];

/**
 * 官方交易时段(只读)。对齐 cTrader `ProtoOASymbol.schedule`(真理之源),
 * 由后端启动 + 每日 03:30 同步;雇员可交易窗口另由平台业务时段控制。
 */
export function OfficialHoursCard({ symbol }: { symbol: AdminSymbol }) {
  const schedule = symbol.tradingSchedule ?? [];
  const holidays = symbol.tradingHolidays ?? [];
  const hasSchedule = schedule.length > 0;
  const hasLegacy = symbol.sessionUtcOpen != null || symbol.sessionUtcClose != null;
  if (!hasSchedule && !hasLegacy && symbol.marketOpen == null) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-brand" />
            官方交易时段 · {symbol.symbolName}
          </span>
          {symbol.marketOpen != null && (
            <Pill tone={symbol.marketOpen ? 'up' : 'down'}>
              {symbol.marketStatus ?? (symbol.marketOpen ? '交易中' : '休市')}
            </Pill>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {hasSchedule ? (
          <div className="space-y-1">
            <div className="flex justify-between gap-4">
              <span className="text-ink-2">展示时区</span>
              <span className="num text-ink-1">
                {symbol.scheduleDisplayTimeZone ?? 'Asia/Shanghai'}（北京）
                {symbol.scheduleTimeZone && (
                  <span className="text-ink-3 ml-1">· 源 {symbol.scheduleTimeZone}</span>
                )}
              </span>
            </div>
            {schedule.map((d) => (
              <div key={d.weekday} className="flex justify-between gap-4">
                <span className="text-ink-2 w-12 shrink-0">
                  {d.label ?? WEEKDAYS[d.weekday] ?? String(d.weekday)}
                </span>
                <span className="num text-ink-1 text-right flex-1">
                  {d.ranges.length
                    ? d.ranges.map((r) => `${r.open}-${r.close}`).join('  ')
                    : '休市'}
                </span>
              </div>
            ))}
          </div>
        ) : hasLegacy ? (
          <div className="flex justify-between gap-4">
            <span className="text-ink-2">国际市场时段</span>
            <span className="num text-ink-1">
              {(symbol.sessionUtcOpen ?? '—') + ' - ' + (symbol.sessionUtcClose ?? '—') + ' UTC'}
            </span>
          </div>
        ) : null}

        {holidays.length > 0 && (
          <div className="pt-2 border-t border-line">
            <div className="text-ink-2 mb-1">节假日休市</div>
            <div className="space-y-0.5">
              {holidays.slice(0, 8).map((h, i) => (
                <div key={`${h.date}-${i}`} className="flex gap-3 text-xs text-ink-2">
                  <span className="num w-20 shrink-0">
                    {h.recurring && h.date.length >= 10 ? h.date.slice(5) : h.date}
                  </span>
                  <span className="flex-1">
                    {h.name}
                    {h.startLocal && h.endLocal && (
                      <span className="num text-ink-3 ml-2">
                        北京 {h.startLocal} – {h.endLocal}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {symbol.marketOpen === false && (
          <div className="rounded-md bg-bg-3 px-3 py-2 text-xs text-ink-2">
            {symbol.marketStatus ?? '当前休市'}
            {symbol.nextOpenAt ? ` · 预计 ${fmtDateTime(symbol.nextOpenAt)} 开盘` : ''}
          </div>
        )}

        <div className="text-xs text-ink-3 leading-relaxed pt-2 border-t border-line">
          官方时段来自 cTrader(只读,不可修改),已换算为<strong className="text-ink-2">北京时间</strong>(与 cTrader 客户端显示一致),每日 03:30 自动同步
          {symbol.scheduleSyncedAt ? `(上次 ${fmtDateTime(symbol.scheduleSyncedAt)})` : ''}。
          雇员实际可交易窗口由<strong className="text-ink-2">平台业务时段</strong>(见「交易时段配置」)控制。
        </div>
      </CardContent>
    </Card>
  );
}
